/**
 * 免单券管理页（C3 验收页）。
 *
 * Phase 4-3 / Stream 4 / Plan §12 / Design §4.4 C3.
 *
 * 功能：
 *  - 状态分布 KPI（available/used/pending_revoke/revoked/expired）
 *  - 列表 + 多过滤（status/userId/voucherType/expired）
 *  - 顶部"批量发券"按钮（modal：textarea 多 userId + expireAt + reason）
 *  - 行操作：撤销（仅 available 状态可见；弹 modal 说明先通知后 24h 作废）
 *  - pending_revoke 行高亮 + 显示 revokeScheduledAt 倒计时
 *
 * 权限：
 *  - voucher:read   (super_admin/operator/finance) — 全页
 *  - voucher:grant  (super_admin/operator) — 发券按钮
 *  - voucher:revoke (super_admin/operator) — 撤销按钮
 *
 * 重要 UX 约束："先通知后作废" — 撤销 modal 必须明确告知用户：
 *  立即触发通知 + 24h 后 cron 完成 作废
 */
import { useMemo, useState } from 'react';
import {
  Card, Table, Form, Input, InputNumber, Button, Space, Tag, Modal,
  Alert, message, Statistic, Row, Col, Select, DatePicker, Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listVouchers, grantVouchers, revokeVoucher, getStatusDistribution,
  type VoucherDto, type VoucherStatus, type ListVouchersFilter,
} from '../../api/vouchers';

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN');
}

function statusColor(s: VoucherStatus): string {
  switch (s) {
    case 'available': return 'green';
    case 'used': return 'blue';
    case 'pending_revoke': return 'orange';
    case 'revoked': return 'red';
    case 'expired': return 'default';
  }
}

function statusLabel(s: VoucherStatus): string {
  switch (s) {
    case 'available': return '可用';
    case 'used': return '已使用';
    case 'pending_revoke': return '撤销中';
    case 'revoked': return '已撤销';
    case 'expired': return '已过期';
  }
}

function VouchersPageInner() {
  const qc = useQueryClient();
  const [filterForm] = Form.useForm<{
    status?: VoucherStatus; userId?: string;
    voucherType?: string; expired?: boolean;
  }>();
  const [filters, setFilters] = useState<ListVouchersFilter>({});

  // grant modal
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantForm] = Form.useForm<{
    userIdsText: string; expireAt: Dayjs; reason: string;
  }>();

  // revoke modal
  const [revokeTarget, setRevokeTarget] = useState<VoucherDto | null>(null);
  const [revokeForm] = Form.useForm<{ reason: string }>();

  const queryParams = useMemo(() => filters, [filters]);
  const listQuery = useQuery({
    queryKey: ['vouchers', queryParams],
    queryFn: () => listVouchers(queryParams),
  });

  const distributionQuery = useQuery({
    queryKey: ['vouchers', 'distribution'],
    queryFn: getStatusDistribution,
  });

  const distMap = useMemo(() => {
    const m: Partial<Record<VoucherStatus, number>> = {};
    for (const it of distributionQuery.data?.items ?? []) {
      m[it.status] = it.count;
    }
    return m;
  }, [distributionQuery.data]);

  const errorHandler = (label: string) =>
    (e: { response?: { data?: { message?: string } }; message?: string }) =>
      message.error(e.response?.data?.message ?? e.message ?? label);

  const grantMutation = useMutation({
    mutationFn: grantVouchers,
    onSuccess: (data) => {
      message.success(`已发出 ${data.voucherIds.length} 张券（用户将自动收到通知）`);
      setGrantOpen(false);
      grantForm.resetFields();
      qc.invalidateQueries({ queryKey: ['vouchers'] });
    },
    onError: errorHandler('发券失败'),
  });

  const revokeMutation = useMutation({
    mutationFn: (vars: { voucherId: string; reason: string }) =>
      revokeVoucher(vars.voucherId, vars.reason),
    onSuccess: (data) => {
      message.success(
        `已 schedule 撤销，预计 ${formatTs(data.revokeScheduledAt)} 完成（用户已收到通知）`,
      );
      setRevokeTarget(null);
      revokeForm.resetFields();
      qc.invalidateQueries({ queryKey: ['vouchers'] });
    },
    onError: errorHandler('撤销失败'),
  });

  function applyFilters() {
    const v = filterForm.getFieldsValue();
    const next: ListVouchersFilter = {};
    if (v.status) next.status = v.status;
    if (v.userId?.trim()) next.userId = v.userId.trim();
    if (v.voucherType?.trim()) next.voucherType = v.voucherType.trim();
    if (v.expired) next.expired = true;
    setFilters(next);
  }

  function resetFilters() {
    filterForm.resetFields();
    setFilters({});
  }

  function submitGrant() {
    grantForm.validateFields().then((v) => {
      const rawIds = v.userIdsText
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      // 去重：粘贴时重复行常见；后端 service 也去重，前端给出可见提示。
      const userIds = Array.from(new Set(rawIds));
      const duplicates = rawIds.length - userIds.length;
      if (userIds.length === 0) {
        message.error('请填至少 1 个 userId');
        return;
      }
      if (userIds.length > 100) {
        message.error(`一次最多 100 个 userId（去重后 ${userIds.length}）`);
        return;
      }
      if (duplicates > 0) {
        message.info(`已自动去重 ${duplicates} 条重复 userId，最终发出 ${userIds.length} 张券`);
      }
      grantMutation.mutate({
        userIds,
        expireAt: v.expireAt.valueOf(),
        reason: v.reason.trim(),
      });
    });
  }

  function submitRevoke() {
    if (!revokeTarget) return;
    revokeForm.validateFields().then((v) => {
      revokeMutation.mutate({
        voucherId: revokeTarget.voucherId,
        reason: v.reason.trim(),
      });
    });
  }

  const columns: ColumnsType<VoucherDto> = [
    { title: 'voucherId', dataIndex: 'voucherId', key: 'vid', width: 200, ellipsis: true },
    { title: 'userId', dataIndex: 'userId', key: 'uid', width: 180, ellipsis: true },
    { title: 'type', dataIndex: 'voucherType', key: 'vt', width: 100 },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 110,
      render: (s: VoucherStatus) => <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>,
    },
    { title: '发出时间', dataIndex: 'earnedAt', key: 'ea', width: 170, render: formatTs },
    { title: '过期', dataIndex: 'expireAt', key: 'exp', width: 170, render: formatTs },
    {
      title: '使用 / 撤销',
      key: 'usage', width: 200,
      render: (_: unknown, row) => {
        if (row.status === 'used') {
          return <span>已用 @ {formatTs(row.usedAt)}<br /><small>{row.appliedToWorkId ?? '-'}</small></span>;
        }
        if (row.status === 'pending_revoke' || row.status === 'revoked') {
          return (
            <span>
              {row.status === 'pending_revoke' ? '待 cron 完成' : '已 revoked'}<br />
              <small>{formatTs(row.revokeScheduledAt)} {row.revokeReason ? `: ${row.revokeReason}` : ''}</small>
            </span>
          );
        }
        return '-';
      },
    },
    { title: 'source', dataIndex: 'source', key: 'src', width: 110 },
    {
      title: '操作', key: 'actions', width: 100,
      render: (_: unknown, row) => (
        row.status === 'available' ? (
          <PermissionGate point={PERMISSIONS.VOUCHER_REVOKE}>
            <Tooltip title="先发通知，24h 后 cron 完成作废">
              <Button
                size="small" danger
                onClick={() => {
                  setRevokeTarget(row);
                  revokeForm.resetFields();
                }}
                data-testid={`voucher-revoke-${row.voucherId}`}
              >
                撤销
              </Button>
            </Tooltip>
          </PermissionGate>
        ) : null
      ),
    },
  ];

  return (
    <Card title="免单券管理（C3）" data-testid="vouchers-page">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Card size="small"><Statistic title="可用" value={distMap.available ?? 0} /></Card>
        </Col>
        <Col span={5}>
          <Card size="small"><Statistic title="已使用" value={distMap.used ?? 0} /></Card>
        </Col>
        <Col span={5}>
          <Card size="small"><Statistic title="撤销中" value={distMap.pending_revoke ?? 0} /></Card>
        </Col>
        <Col span={5}>
          <Card size="small"><Statistic title="已撤销" value={distMap.revoked ?? 0} /></Card>
        </Col>
        <Col span={4}>
          <Card size="small"><Statistic title="已过期" value={distMap.expired ?? 0} /></Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Form
          form={filterForm} layout="inline"
          data-testid="vouchers-filter-form"
          onFinish={applyFilters}
        >
          <Form.Item name="status" label="状态">
            <Select placeholder="任意" allowClear style={{ width: 130 }}
              options={[
                { value: 'available', label: '可用' },
                { value: 'used', label: '已使用' },
                { value: 'pending_revoke', label: '撤销中' },
                { value: 'revoked', label: '已撤销' },
                { value: 'expired', label: '已过期' },
              ]}
            />
          </Form.Item>
          <Form.Item name="userId" label="userId">
            <Input style={{ width: 200 }} placeholder="精确匹配" />
          </Form.Item>
          <Form.Item name="voucherType" label="type">
            <Select placeholder="任意" allowClear style={{ width: 130 }}
              options={[{ value: 'free_book', label: 'free_book' }]}
            />
          </Form.Item>
          <Form.Item name="expired" label="过期筛选" valuePropName="checked">
            <Select style={{ width: 100 }} allowClear placeholder="任意"
              options={[{ value: true, label: '仅过期' }]}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">应用</Button>
              <Button onClick={resetFilters}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
        <PermissionGate point={PERMISSIONS.VOUCHER_GRANT}>
          <Button
            type="primary"
            onClick={() => setGrantOpen(true)}
            data-testid="vouchers-grant-btn"
          >
            批量发券
          </Button>
        </PermissionGate>
      </Space>

      <Table<VoucherDto>
        rowKey="voucherId"
        loading={listQuery.isLoading}
        dataSource={listQuery.data?.items ?? []}
        columns={columns}
        pagination={false}
        rowClassName={(row) => row.status === 'pending_revoke' ? 'voucher-row-pending' : ''}
        data-testid="vouchers-table"
      />

      {/* 批量发券 modal */}
      <Modal
        title="批量发券"
        open={grantOpen}
        onCancel={() => { setGrantOpen(false); grantForm.resetFields(); }}
        onOk={submitGrant}
        confirmLoading={grantMutation.isPending}
        destroyOnClose
        width={560}
        data-testid="vouchers-grant-modal"
      >
        <Alert
          type="info" showIcon
          message="发券成功后，content-pipeline 会自动给每个用户触发 voucher-granted 订阅消息通知"
          style={{ marginBottom: 16 }}
        />
        <Form form={grantForm} layout="vertical" preserve={false}>
          <Form.Item
            label="userIds（每行 1 个 / 逗号分号空格分隔均可，最多 100 个）"
            name="userIdsText"
            rules={[{ required: true, message: '请填 userId 列表' }]}
          >
            <Input.TextArea rows={6} placeholder="u-1&#10;u-2&#10;u-3" />
          </Form.Item>
          <Form.Item
            label="过期时间"
            name="expireAt"
            rules={[
              { required: true, message: '请选过期时间' },
              {
                validator: async (_, v: Dayjs | undefined) => {
                  if (!v) return;
                  if (v.valueOf() <= Date.now()) {
                    throw new Error('过期时间必须晚于现在');
                  }
                },
              },
            ]}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              disabledDate={(d) => d.isBefore(dayjs(), 'day')}
            />
          </Form.Item>
          <Form.Item
            label="发券原因（5..200 字符，会写审计 + 通知内容）"
            name="reason"
            rules={[
              { required: true, message: '请填发券原因' },
              { min: 5, max: 200, message: '5..200 字符' },
            ]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* 撤销 modal */}
      <Modal
        title={`撤销券 ${revokeTarget?.voucherId ?? ''}`}
        open={revokeTarget !== null}
        onCancel={() => { setRevokeTarget(null); revokeForm.resetFields(); }}
        onOk={submitRevoke}
        confirmLoading={revokeMutation.isPending}
        destroyOnClose
        data-testid="vouchers-revoke-modal"
      >
        <Alert
          type="warning" showIcon
          message="先通知后作废 (两段式)"
          description={
            <span>
              <strong>立即</strong>：触发 voucher-revoked 订阅消息通知用户<br />
              <strong>24h 后</strong>：content-pipeline 后台 cron 把状态 flip 到 revoked<br />
              <small>注意：24h 窗口内用户仍可核销此券（设计上未阻止）</small>
            </span>
          }
          style={{ marginBottom: 16 }}
        />
        <Form form={revokeForm} layout="vertical" preserve={false}>
          <Form.Item
            label="撤销原因（5..200 字符）"
            name="reason"
            rules={[
              { required: true, message: '请填撤销原因' },
              { min: 5, max: 200, message: '5..200 字符' },
            ]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

export default function VouchersPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.VOUCHER_READ}
      fallback={
        <Card>
          <Alert
            type="error" showIcon message="无访问权限"
            description="免单券管理（C3）需要 voucher:read 权限，请联系超管。"
          />
        </Card>
      }
    >
      <VouchersPageInner />
    </PermissionGate>
  );
}
